import { i18n as I18nInstance } from 'i18next';
import { AddressCountryCodeEU } from '@bloomays-lib/types.shared';

export const getSocietyKBISLabel = (
  society: { isEuropeanSociety: boolean; isForeignSociety: boolean; isMicroEntrepreneur: boolean },
  i18n: I18nInstance,
) => {
  if (society.isEuropeanSociety) {
    return i18n.t('KBISLabel.EuropeanSociety', { ns: 'society' });
  }
  if (society.isForeignSociety) {
    return i18n.t('KBISLabel.ForeignSociety', { ns: 'society' });
  }
  if (society.isMicroEntrepreneur) {
    return i18n.t('KBISLabel.MicroEntrepreneur', { ns: 'society' });
  }
  return i18n.t('KBISLabel.FrenchSociety', { ns: 'society' });
};

export const getSocietyTaxesLabel = (
  society: { isEuropeanSociety: boolean; isForeignSociety: boolean; isMicroEntrepreneur: boolean },
  i18n: I18nInstance,
) => {
  if (society.isEuropeanSociety) {
    return i18n.t('TaxesLabel.EuropeanSociety', { ns: 'society' });
  }
  if (society.isForeignSociety) {
    return i18n.t('TaxesLabel.ForeignSociety', { ns: 'society' });
  }
  if (society.isMicroEntrepreneur) {
    return i18n.t('TaxesLabel.MicroEntrepreneur', { ns: 'society' });
  }
  return i18n.t('TaxesLabel.FrenchSociety', { ns: 'society' });
};

export const getSocietyInsuranceLabel = (
  society: { isEuropeanSociety: boolean; isForeignSociety: boolean; isMicroEntrepreneur: boolean },
  i18n: I18nInstance,
) => {
  if (society.isEuropeanSociety) {
    return i18n.t('InsuranceLabel.EuropeanSociety', { ns: 'society' });
  }
  if (society.isForeignSociety) {
    return i18n.t('InsuranceLabel.ForeignSociety', { ns: 'society' });
  }
  if (society.isMicroEntrepreneur) {
    return i18n.t('InsuranceLabel.MicroEntrepreneur', { ns: 'society' });
  }
  return i18n.t('InsuranceLabel.FrenchSociety', { ns: 'society' });
};

export const getSocietyStatusInfos = (
  status: string | undefined,
): {
  isEuropeanSociety: boolean;
  isForeignSociety: boolean;
  isMicroEntrepreneur: boolean;
  isStatusUnknown: boolean;
} => {
  let isEuropeanSociety = false;
  let isForeignSociety = false;
  let isMicroEntrepreneur = false;
  switch (status) {
    case 'Auto Entrepreneur':
    case 'EI':
      isEuropeanSociety = false;
      isMicroEntrepreneur = true;
      isForeignSociety = false;
      break;
    case 'SUE':
      isEuropeanSociety = true;
      isMicroEntrepreneur = false;
      isForeignSociety = false;
      break;
    case 'SHUE':
      isEuropeanSociety = false;
      isMicroEntrepreneur = false;
      isForeignSociety = true;
      break;
    default:
      isEuropeanSociety = false;
      isMicroEntrepreneur = false;
      isForeignSociety = false;
      break;
  }
  return { isEuropeanSociety, isForeignSociety, isMicroEntrepreneur, isStatusUnknown: !status };
};

/**
 * Filtre commun (Bloomer et Client, TEC-3218) des pays sélectionnables dans un select "Pays",
 * dérivé du statut juridique de la société : formes françaises → France uniquement, société UE
 * (`SUE`) → UE hors France, société hors UE (`SHUE`) → hors UE. Utiliser avec `getSocietyStatusInfos`.
 *
 * Tant qu'aucun statut n'est renseigné, tous les pays restent sélectionnables (TEC-3318) : le statut
 * est facultatif, et restreindre à la France par défaut empêchait de saisir une société étrangère.
 */
export const isCountryAllowedForSocietyStatus = (
  statusInfos: { isEuropeanSociety: boolean; isForeignSociety: boolean; isStatusUnknown?: boolean },
  countryCode: string,
): boolean => {
  if (statusInfos.isStatusUnknown) {
    return true;
  }
  if (statusInfos.isForeignSociety) {
    return countryCode !== 'FR' && !AddressCountryCodeEU.includes(countryCode);
  }
  if (statusInfos.isEuropeanSociety) {
    return countryCode !== 'FR' && AddressCountryCodeEU.includes(countryCode);
  }
  return countryCode === 'FR';
};
